<template>
  <div
    v-if="isGrantStage"
    class="d-flex align-center"
    data-testid="draw-grant-action"
  >
    <SoTooltip :disabled="canGrant" :text="disabledReason">
      <SoButton
        title="Allocate prizes"
        type="primary"
        :disabled="!canGrant"
        data-testid="grant-prizes-btn"
        @click="openDialog"
      />
    </SoTooltip>

    <SoDialog
      v-model="showGrantDialog"
      card-title="Allocate prizes?"
      :max-width="540"
      data-testid="grant-prizes-dialog"
    >
      <p>
        This allocates credit prizes only: account credit winners are credited
        today, and Year-of-Energy winners begin 12 monthly instalments next
        month. Winners of other prize types are not allocated by this action.
      </p>
      <div class="prerequisites mt-4">
        <div v-for="item in prerequisites" :key="item.key" class="prerequisite">
          <SoCheckbox
            :id="item.key"
            v-model="checked[item.key]"
            :data-testid="`grant-prereq-${item.key}`"
            top-aligned
          />
          <label :for="item.key">{{ item.label }}</label>
        </div>
      </div>
      <div class="d-flex justify-end mt-4">
        <SoButton
          title="Cancel"
          class="mr-2"
          large
          @click="showGrantDialog = false"
        />
        <SoButton
          title="Allocate prizes"
          type="primary"
          large
          :disabled="!allConfirmed"
          :loading="grantMutation.isPending"
          data-testid="grant-prizes-confirm-btn"
          @click="confirmGrant"
        />
      </div>
    </SoDialog>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import { useSnackbarStore } from "@/stores/snackbar"
  import errorHandler from "@/services/ErrorService/errorHandler"
  import { LoyaltyMutator } from "@/mutators/loyalty/LoyaltyMutator"
  import { PrizeDrawStatus } from "@/api/backofficeRest/generated/model"
  import {
    arePrizesGranted,
    IDrawWinner,
  } from "@/models/operations/draw/drawWinner.types"

  // Prerequisites the admin must confirm before the irreversible award.
  const PREREQUISITES = [
    {
      key: "prizeSwap",
      label:
        "The prize-swap request deadline has passed — no winner is still waiting on an alternative-prize decision.",
    },
    {
      key: "reviewed",
      label:
        "The winner list is final — any rejected slots have been topped up and reviewed.",
    },
    {
      key: "irreversible",
      label:
        "I understand this awards prizes and hands off to reward distribution, and can’t be undone.",
    },
  ] as const

  // Statuses (once entry has closed) before notify where Allocate shows as a
  // disabled preview — canGrant covers the actionable states.
  const PRE_GRANT_STATUSES: ReadonlySet<PrizeDrawStatus> = new Set([
    PrizeDrawStatus.ENTRY_CLOSED,
    PrizeDrawStatus.CALCULATING_WINNERS,
    PrizeDrawStatus.WINNERS_CALCULATED,
  ])

  export default defineComponent({
    components: { SoButton, SoCheckbox, SoDialog, SoTooltip },
    props: {
      drawId: {
        type: String,
        required: true,
      },
      status: {
        type: String as PropType<PrizeDrawStatus | null>,
        default: null,
      },
      winners: {
        type: Array as PropType<IDrawWinner[]>,
        default: () => [],
      },
    },
    emits: ["changed"],
    data() {
      return {
        snackbar: useSnackbarStore(),
        grantMutation: LoyaltyMutator.grantPrizes(),
        showGrantDialog: false,
        prerequisites: PREREQUISITES,
        checked: {} as Record<string, boolean>,
      }
    },
    computed: {
      // Prizes have already been granted once any winner is REWARDED.
      alreadyGranted(): boolean {
        return arePrizesGranted(this.winners)
      },
      // Grant follows notify, while prizes are still ungranted. WINNERS_PUBLISHED
      // is the same point after the publication date passed pre-grant (it implies
      // notified); alreadyGranted hides it once any winner is REWARDED.
      canGrant(): boolean {
        return (
          (this.status === PrizeDrawStatus.WINNERS_NOTIFIED ||
            this.status === PrizeDrawStatus.WINNERS_PUBLISHED) &&
          !this.alreadyGranted
        )
      },
      // canGrant stays the single source of truth for the actionable states;
      // PRE_GRANT_STATUSES adds the post-entry-close stages where it shows as a
      // disabled preview so the upcoming action is discoverable.
      isGrantStage(): boolean {
        return (
          this.canGrant ||
          (this.status !== null && PRE_GRANT_STATUSES.has(this.status))
        )
      },
      disabledReason(): string {
        return this.canGrant ? "" : "Notify winners before allocating prizes"
      },
      allConfirmed(): boolean {
        return this.prerequisites.every((item) => this.checked[item.key])
      },
    },
    methods: {
      openDialog() {
        this.checked = {}
        this.showGrantDialog = true
      },
      async confirmGrant() {
        try {
          await this.grantMutation.mutateAsync({ drawId: this.drawId })
        } catch (error) {
          errorHandler.showCaughtError(
            error,
            "Failed to grant prizes. Please try again.",
          )
          return
        }
        this.snackbar.addSuccess("Prizes granted")
        this.showGrantDialog = false
        this.$emit("changed")
      },
    },
  })
</script>

<style scoped>
  .prerequisite {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }
  /* Full-sentence prerequisites read as body copy, not the larger checkbox-label size. */
  .prerequisite > label {
    font-size: 1rem;
    cursor: pointer;
  }
</style>
